import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["border", "children", "className", "fwdRef", "indented", "size", "stacked"];
import React, { createContext, forwardRef, useState } from 'react';
import { buildVersionedClassNames } from '@op/opds.opux-utilities';
export const ListContext = /*#__PURE__*/createContext({
  border: true,
  labeled: false,
  onLabel: () => {},
  size: 'default'
});

/**
 * _A vertical list of list items that can link elsewhere or trigger actions._
 *
 * ***
 *
 * A list of list items that controls their layout.
 * Expects all children to be OPUX [`ListItem`](#/Lists%20%26%20Tables/ListItem) components.
 * If any child sets a `label` prop, the component is rendered as a `<dl>`.
 * Otherwise `<ul>` is used (the default behaviour). Therefore, items with and
 * without a label cannot programmatically be mixed in a single list.
 *
 * Additional props (including `ref`) will be forwarded to the wrapping HTML element.
 */
export function List(_ref) {
  let {
      border: propBorder = true,
      children,
      className,
      fwdRef,
      indented,
      size = 'default',
      stacked
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [labeled, setLabeled] = useState(false);
  let border = propBorder;
  if (propBorder === 'none') {
    border = false;
  } else if (propBorder === 'all') {
    border = true;
  }
  const cn = buildVersionedClassNames('ds-list', className, {
    ["ds-list--border-" + border]: typeof border === 'string',
    'ds-list--border-none': !border,
    'ds-list--indented': border && border !== 'button' && indented,
    'ds-list--labeled': labeled,
    'ds-list--stacked': labeled && stacked,
    ["ds-list--" + size]: size && size !== 'default'
  });
  if (labeled) {
    return /*#__PURE__*/React.createElement("dl", _extends({}, props, {
      className: cn,
      ref: fwdRef
    }), /*#__PURE__*/React.createElement(ListContext.Provider, {
      value: {
        border,
        labeled,
        onLabel: () => setLabeled(true),
        size
      }
    }, children));
  } else {
    return /*#__PURE__*/React.createElement("ul", _extends({}, props, {
      className: cn,
      ref: fwdRef
    }), /*#__PURE__*/React.createElement(ListContext.Provider, {
      value: {
        border,
        labeled,
        onLabel: () => setLabeled(true),
        size
      }
    }, children));
  }
}
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(List, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'List';
export default Fwd;